import { useEffect, useState } from "react";
import { GLOBAL_IMAGES } from "../../assets/asset";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from "chart.js";
import { Link, useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";
import Header from "./Header";
import { getCurrentUnlockedYearId } from "../../utils/basicPlanAccess";

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const COLORS = ["#5CB85C", "#FF8A00", "#E74C3C"];

const parseMetricValue = (value) => {
  const parsed = Number.parseFloat(String(value ?? "").replace("%", ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildPieDataFromAverageReport = (userAverageReport = []) => {
  if (!Array.isArray(userAverageReport) || userAverageReport.length === 0) {
    return null;
  }

  const labels = [];
  const values = [];

  userAverageReport.forEach((entry) => {
    const [label, rawValue] = Object.entries(entry || {})[0] || [];
    if (!label) return;

    labels.push(label);
    values.push(parseMetricValue(rawValue));
  });

  if (!labels.length || values.every((value) => value === 0)) {
    return null;
  }

  return {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: COLORS.slice(0, labels.length),
        borderColor: "transparent",
      },
    ],
  };
};

const buildPieData = (response) => {
  if (!response || typeof response !== "object") {
    return null;
  }

  if (Array.isArray(response.answer_summary) && response.answer_summary.length) {
    return {
      labels: response.answer_summary.map((item) => item.level),
      datasets: [
        {
          data: response.answer_summary.map((item) => parseMetricValue(item.percentage)),
          backgroundColor: COLORS.slice(0, response.answer_summary.length),
          borderColor: "transparent",
        },
      ],
    };
  }

  return buildPieDataFromAverageReport(response.useraveragereport);
};

export default function ReportDetails() {
  const location = useLocation();
  const [reportData, setReportData] = useState(location.state?.report || {});
  const [pieData, setPieData] = useState(() => buildPieData(location.state));
  const [totalQuestions, setTotalQuestions] = useState(
    location.state?.totalQuestions || location.state?.report?.Totlequestions || "0"
  );
  const [accuracy, setAccuracy] = useState(
    location.state?.accuracy || location.state?.report?.Accuracy || "0%"
  );
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(() => {
    if (location.state?.yearstandardId) {
      return Number.parseInt(location.state.yearstandardId, 10);
    }
    const parentRaw = localStorage.getItem("user");
    const parent = parentRaw ? JSON.parse(parentRaw) : null;
    return getCurrentUnlockedYearId(parent) || 7;
  });
  const [dob, setDOB] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  /* -------------------------- helpers -------------------------- */
  const getPracticedays = () => reportData?.["Practice days"] || "0";
  const getProblemsAnswered = () => reportData?.["Problems answered"] || "0";
  const getTotalQuestions = () =>
    totalQuestions || reportData?.Totlequestions || "0";
  const getAccuracy = () => accuracy || reportData?.Accuracy || "0%";
  const getBadgesAssigned = () => reportData?.Badgesassigned || "0";
  const getTotalMarks = () => reportData?.total_marks || "0";
  const getObtainedMarks = () => reportData?.obtained_marks || "0";
  const isCompactReport =
    typeof window !== "undefined" && window.innerWidth <= 640;

  /* -------------------------- fetch years ------------------------ */
  // useEffect(() => {
  //   const fetchYearsAsync = async () => {
  //     const rawToken = localStorage.getItem("token");
  //     const token = rawToken ? JSON.parse(rawToken) : null;
  //     if (!token) return;

  //     try {
  //       const res = await fetch(
  //         `${process.env.REACT_APP_BASE_URL}studentYearHistory`,
  //         {
  //           headers: {
  //             Authorization: `${process.env.REACT_APP_AUTH_TOKEN_DEV}`,
  //             "Content-Type": "application/json",
  //             jwt: token,
  //           },
  //         }
  //       );
  //       const data = await res.json();
  //       if (data?.data?.length) {
  //         setYears(data.data);
  //         setSelectedYear(data.data[data.data.length - 1][0]);
  //       }
  //     } catch (err) {
  //       console.error("Failed to fetch years", err);
  //       toast.error("Failed to fetch years data");
  //     }
  //   };

  //   // fetchYearsAsync();
  // }, []);

  /* ---------------------- report mutation ---------------------- */
  const parentsReportMutation = useMutation({
    mutationKey: ["getReport"],
    mutationFn: (payload) => axiosInstance.post("api/parentsreport", payload),
    onSuccess: ({ data: res }) => {
      setIsLoading(false);
      console.log("[ReportSection DEBUG] onSuccess res:", res);

      if (!res?.report) {
        toast.error(res?.message || "Invalid response");
        return;
      }

      setReportData(res.report);
      setTotalQuestions(res.report.Totlequestions || "0");
      setAccuracy(res.report.Accuracy || "0%");
      setPieData(buildPieData(res));
    },
    onError: (e) => {
      setIsLoading(false);
      console.log("[ReportSection DEBUG] onError e:", e);
      toast.error(e?.response?.data?.message || "Something went wrong");
    },
  });

  /* --------------------------- dob init ------------------------- */
  useEffect(() => {
    const dobFromState = location.state?.dob;
    const parentRaw = localStorage.getItem("user");
    const parent = parentRaw ? JSON.parse(parentRaw) : null;
    setDOB(dobFromState || parent?.parents_date_of_birth || "");
  }, [location]);

  /* --------------------------- report fetch on dob/year change ------------------------- */
  useEffect(() => {
    if (selectedYear === null) return;

    const rawToken = localStorage.getItem("token");
    const token = rawToken ? JSON.parse(rawToken) : null;
    if (!token) return;

    setIsLoading(true);
    parentsReportMutation.mutate({
      dateofbirth: dob || "",
      yearstandardId: String(selectedYear),
    });
  }, [selectedYear, dob]);

  /* --------------------------- render -------------------------- */
  return (
    <div
      className="report-details-page min-h-screen w-full flex flex-col items-center font-mali"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
      role="main"
      aria-labelledby="years-dashboard-title"
    >
      <Header dot={false} />

      {/* Year selection dropdown */}
      {years.length > 1 && (
        <div className="report-details-year-select mt-8 w-11/12 font-mulish">
          <label htmlFor="year-select" className="sr-only">
            Select Year
          </label>
          <select
            id="year-select"
            className="bg-white p-1 rounded-xl shadow-md text-center backdrop-blur-lg shadow-indigo-100"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {years.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Cards for Progress Report and Badges */}
      <div className="report-details-shortcuts w-11/12 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-10 py-8">
        <Link
          to="/report/ai-report"
          aria-label="Progress Report"
          className="report-details-shortcut-card flex flex-col items-center justify-center px-5 py-6 bg-white/50 rounded-3xl border-2 border-white shadow-sm hover:bg-white/80 w-[180px] sm:w-[180px] md:w-[185px] min-w-[50vw] sm:min-w-0 h-[130px]"
        >
          <img
            src={GLOBAL_IMAGES.progressReportImage}
            alt="AI Progress Report"
            className="h-10 w-10 mb-1"
          />
          <span className="font-semibold text-base text-gray-900">
            Progress Report
          </span>
          <span className="text-sm text-transparent mt-1.5">.</span>
        </Link>

        <Link
          to="/report/badges"
          aria-label="Badges earned"
          className="report-details-shortcut-card flex flex-col items-center justify-center px-5 py-6 bg-white/50 rounded-3xl border-2 border-white shadow-sm hover:bg-white/80 w-[180px] sm:w-[180px] md:w-[185px] min-w-[50vw] sm:min-w-0 h-[130px]"
        >
          <span className="inline-flex items-center justify-center h-10 w-10 mb-1">
            <img src={GLOBAL_IMAGES.badge} alt="Badge" />
          </span>
          <span className="font-semibold text-base text-gray-900">
            Badges earned
          </span>
          <span className="text-sm text-gray-600 mt-1.5">
            Previous week: {getBadgesAssigned()}
          </span>
        </Link>
      </div>

      {/* Main Content */}
      <div className="report-details-content py-4 p-2 font-sans flex-grow mb-8 flex flex-col gap-3 overflow-auto ring-white ring-4 backdrop-blur-sm bg-white/40 w-11/12 rounded-3xl px-6 mt-8 mx-9">
        {isLoading && (
          <div
            className="flex items-center justify-center p-8"
            role="status"
            aria-live="polite"
          >
            <p className="text-gray-600">Loading report data...</p>
          </div>
        )}

        {/* Performance Pie Chart */}
        <div className="report report-details-section bg-white/60 h-fit w-full rounded-md p-4">
          <p
            className="report-details-section-title font-bold font-mulish text-xl bg-primary-dark-blue px-4 py-2 rounded-md w-full max-w-96 text-white"
            id="performance-chart-title"
          >
            Performance Level Chart
          </p>

          {pieData ? (
            <div
              className="report-details-chart-wrap flex items-center justify-center p-4"
              aria-labelledby="performance-chart-title"
            >
              <div className="report-details-chart w-[400px] h-[250px] max-w-full">
                <Pie
                  data={pieData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: isCompactReport ? "bottom" : "right",
                        labels: {
                          font: { size: isCompactReport ? 11 : 16, weight: "bold" },
                          padding: isCompactReport ? 8 : 15,
                          usePointStyle: true,
                          pointStyle: "rect",
                          boxWidth: isCompactReport ? 10 : 15,
                          boxHeight: isCompactReport ? 10 : 15,
                        },
                      },
                      title: {
                        display: false,
                      },
                    },
                  }}
                  aria-label="Performance Level Pie Chart"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-gray-500">No chart data available</p>
            </div>
          )}
        </div>

        {/* Practice Days */}
        <div className="report report-details-section bg-white/60 h-fit w-full rounded-md p-4">
          <p className="report-details-section-title font-bold font-mulish text-xl bg-primary-dark-blue px-4 py-2 rounded-md w-96 text-white">
            Practice Days
          </p>
          <div className="report-details-row-wrap p-4">
            <div className="report-details-row flex gap-10 px-10 font-bold font-mulish text-xl">
              <p className="flex-1 border-b-2 border-b-[#4D54FF] px-6 pb-1 rounded-lg">
                Practice Days
              </p>
              <p className="flex-1 border-b-2 border-b-[#4D54FF] px-6 pb-1 rounded-lg">
                {getPracticedays()}
              </p>
              <p className="flex-1" />
            </div>
          </div>
          <div className="report-details-row-wrap p-4">
            <div className="report-details-row flex px-10 font-bold font-mulish text-xl">
              <p className="flex-1 border-b-2 px-6 pb-1 border-yellow-300">
                Problems Answered
              </p>
              <p className="flex-1 px-6 border-b-2 pb-1 border-yellow-300">
                {getProblemsAnswered()} / {getTotalQuestions()}
              </p>
              <p className="flex-1" />
            </div>
          </div>
        </div>

        {/* Student Report */}
        <div className="report report-details-section bg-white/60 h-fit w-full rounded-md p-4">
          <p className="report-details-section-title font-bold font-mulish text-xl bg-primary-dark-blue px-4 py-2 rounded-md w-96 text-white">
            Student Report
          </p>

          {/* header row */}
          <div className="report-details-row-wrap p-4">
            <div className="report-details-row flex gap-10 px-10 font-bold font-mulish text-xl">
              <p className="flex-1 border-b-2 border-b-[#4D54FF] px-6 pb-1 rounded-lg">
                Metric
              </p>
              <p className="flex-1 border-b-2 border-b-[#4D54FF] px-6 pb-1 rounded-lg">
                Value
              </p>
              <p className="flex-1" />
            </div>
          </div>

          {/* rows */}
          {[
            ["Total Questions", getTotalQuestions()],
            ["Accuracy", getAccuracy()],
            ["Total Marks", getTotalMarks()],
            ["Obtained Marks", getObtainedMarks()],
          ].map(([label, value]) => (
            <div className="report-details-row-wrap p-4" key={label}>
              <div className="report-details-row flex px-10 font-bold font-mulish text-xl">
                <p className="flex-1 border-b-2 px-6 pb-1 border-yellow-300">
                  {label}
                </p>
                <p className="flex-1 px-6 border-b-2 pb-1 border-yellow-300">
                  {value}
                </p>
                <p className="flex-1" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
